import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  OnInit,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Dialog } from '@angular/cdk/dialog';
import {
  CompleteAppointmentService,
  CreateAppointmentComponent,
  ScheduleCalendarComponent,
  ScheduleLegendDialogComponent,
  SchedulesService,
} from '@libs/schedule';
import { AppointmentStatusColorV2 } from '@ffd/api-client';
import { NurturingStore } from '@libs/nurturing';
import { DatePickerComponent, DropdownTriggerDirective, RightSidebarComponent, SidebarPage } from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { NgClass } from '@angular/common';
import { Store } from '@ngrx/store';
import {
  AppointmentActionType,
  createAppointmentActions,
  NurturingFeature,
  ScheduleCalendarTimeWithOperatory,
  selectAppointmentActionType,
  selectIsEaglesoftPms,
  selectStatusOptions,
} from '@libs/store';
import { DateTime } from 'luxon';
import { DataState, routes } from '@libs/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationService } from '@libs/shared-services';
import { AddTreatmentModalService } from '@libs/charting';
import { MyCollaborationsSidebarComponent } from '../my-collaborations-sidebar';
import {
  MY_COLLABORATION_SIDEBAR_APPOINTMENT,
  MY_COLLABORATION_SIDEBAR_VISIBLE,
} from '../my-collaborations-sidebar/my-collaborations-sidebar-token';
import { EventDetailsSidebarComponent, EVENT_DETAILS_SIDEBAR_EVENT } from '../event-details-sidebar';
import { ScheduleFilterComponent } from './schedule-filter/schedule-filter.component';

enum HomeScreenTab {
  MyCollaborations = 'my-collabs',
  NewSchedule = 'new-schedule',
  EventDetails = 'event-details',
}

@Component({
  selector: 'lib-home-screen',
  imports: [
    RightSidebarComponent,
    RouterModule,
    ScheduleCalendarComponent,
    DatePickerComponent,
    TranslateModule,
    DropdownTriggerDirective,
    SvgIconComponent,
    NgClass,
    ScheduleFilterComponent,
  ],
  templateUrl: './home-screen.component.html',
  styleUrl: './home-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AddTreatmentModalService, CompleteAppointmentService],
})
export class HomeScreenComponent implements OnInit {
  private readonly screenService = inject(SchedulesService);
  private readonly injector = inject(Injector);
  private readonly store = inject(Store);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly navigationService = inject(NavigationService);
  private readonly dialog = inject(Dialog);
  private readonly addTreatmentModalService = inject(AddTreatmentModalService);
  private readonly nurturingStore = inject(NurturingStore);

  @ViewChild(ScheduleCalendarComponent) private readonly scheduleCalendarComponent!: ScheduleCalendarComponent;

  protected readonly isShowcase = computed(() => this.screenService.viewModel().siteId === routes.showcaseSiteId);
  protected readonly isToday = computed(() => this.screenService.viewModel().isToday);
  protected readonly myDay = computed(() => this.screenService.viewModel().myDay);
  protected readonly selectedOperatories = computed(() => this.screenService.viewModel().selectedOperatoryAppointments);
  protected readonly operatoriesDropdownElements = computed(() => this.screenService.viewModel().operatoriesDropdownElements);
  protected readonly dropdownSelectedOperatories = computed(() => this.screenService.viewModel().selectedOperatoryIds);
  protected readonly date = computed(() => this.screenService.viewModel().selectedDate);
  protected readonly selectedAppointment = computed(() => this.screenService.viewModel().selectedAppointment);
  protected readonly selectedEvent = computed(() => this.screenService.viewModel().selectedEvent);
  protected showTentative = computed(() => this.screenService.viewModel().showTentative);
  protected isCompactView = computed(() => this.screenService.viewModel().isCompactView);
  protected hidePatientName = computed(() => this.screenService.viewModel().hidePatientName);

  protected appointmentActionType = this.store.selectSignal(selectAppointmentActionType);
  protected readonly isEaglesoftPms = this.store.selectSignal(selectIsEaglesoftPms);
  protected readonly appointmentIsEdited = computed(
    () => this.appointmentActionType() === AppointmentActionType.Create || this.appointmentActionType() === AppointmentActionType.Edit,
  );
  protected setActivePage: Signal<SidebarPage> = computed(() => {
    if (this.appointmentIsEdited()) {
      return this.newAppointmentPage;
    }
    if (this.selectedEvent()) {
      return this.eventDetailsPage;
    }
    return this.myCollabPage;
  });

  protected readonly isScheduleLoadingSignal = this.screenService.isScheduleLoadingSignal;
  protected readonly areTreatmentsLoaded = computed(() => {
    const treatmentsState = this.addTreatmentModalService.selectSignal((state) => state.treatments)();
    return treatmentsState.state === DataState.Fetched && (treatmentsState.data ?? []).length > 0;
  });

  /** Signal-backed so sidebar content can react to the sidebar collapsing, which does not unmount it. */
  protected readonly sidebarVisible = signal(false);

  public myCollabPage: SidebarPage = {
    id: HomeScreenTab.MyCollaborations,
    iconKey: computed(() => (this.selectedAppointment() ? 'px-24-time' : 'px-24-collab')),
    disposable: false,
    component: MyCollaborationsSidebarComponent,
    injector: this.createInjector(),
  };

  public newAppointmentPage: SidebarPage = {
    id: HomeScreenTab.NewSchedule,
    iconKey: signal('px-24-time'),
    disposable: false,
    component: CreateAppointmentComponent,
    injector: this.injector,
  };

  public eventDetailsPage: SidebarPage = {
    id: HomeScreenTab.EventDetails,
    iconKey: signal('px-24-time'),
    disposable: false,
    component: EventDetailsSidebarComponent,
    injector: this.createEventDetailsInjector(),
  };

  protected readonly HomeScreenTab = HomeScreenTab;

  public ngOnInit(): void {
    // Returning to the schedule is what step 3 was nudging the user towards, so arriving here
    // completes it. No-ops unless the feature is currently enabled and not temporarily dismissed.
    this.nurturingStore.dismissNurturingFeature(NurturingFeature.GuidedTourStep3ReturnToScheduleNudge);

    this.activatedRoute.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.screenService.reset();
      this.screenService.init();
      this.addTreatmentModalService.init();
    });

    this.handleQueryParams();
  }

  private handleQueryParams(): void {
    this.activatedRoute.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const dateParam = params.get('date');
      const patientIdParam = params.get('patientId');

      if (dateParam) {
        const parsedDate = DateTime.fromISO(dateParam);
        if (parsedDate.isValid) {
          this.screenService.setDate(parsedDate);
          this.selectAppointmentByPatientId(patientIdParam);
        }
      }
    });
  }

  private selectAppointmentByPatientId(patientId: string | null): void {
    if (!patientId) return;

    this.screenService.scheduleState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((scheduleState) => {
      if (scheduleState === DataState.Fetched) {
        const appointmentId = this.screenService.getFirstAppointmentIdByPatientId(patientId);
        if (appointmentId) {
          this.screenService.setAppointmentId(appointmentId);
          // Wait for the view to update before scrolling
          setTimeout(() => {
            this.scheduleCalendarComponent?.scrollToAppointment(appointmentId);
          }, 100);
        }
      }
    });
  }

  protected onDateChange(date: DateTime | null): void {
    this.screenService.setDate(date ?? DateTime.now());
    this.scheduleCalendarComponent.scrollIntoView(this.isToday());
  }

  protected onTodayClick(): void {
    this.screenService.setDate(DateTime.now());
    this.scheduleCalendarComponent.scrollIntoView(true);
  }

  protected onAppointmentSelect(appointmentId: string): void {
    this.screenService.setAppointmentId(appointmentId);
  }

  protected onAppointmentClick(): void {
    this.sidebarVisible.set(true);
  }

  protected onAppointmentDoubleClick(): void {
    const patientId = this.selectedAppointment()?.patientId;
    if (!patientId) return;

    this.navigationService.navigate('chartingOnSiteForPatient', false, [this.screenService.viewModel().siteId, patientId]);
  }

  protected onEventClick(eventId: string): void {
    this.screenService.setEventId(eventId);
    this.sidebarVisible.set(true);
  }

  protected onNewAppointmentFromCalendar(timeWithOperatory: ScheduleCalendarTimeWithOperatory): void {
    this.screenService.setAppointmentId(null);
    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
    this.sidebarVisible.set(true);
    setTimeout(() => {
      this.screenService.setNewAppointmentWithCalendar(timeWithOperatory);
    });
  }

  public onNewAppointmentWithButton(): void {
    this.screenService.setAppointmentId(null);
    this.sidebarVisible.set(true);
    setTimeout(() => {
      this.screenService.setNewAppointmentWithButton();
    });
    this.scheduleCalendarComponent.scrollToCurrentTime();
  }

  protected onVisibilityChange(visible: boolean): void {
    this.sidebarVisible.set(visible);
  }

  protected onTentativeFilterChange(showTentative: boolean): void {
    this.screenService.setShowTentative(showTentative);
  }

  protected onCompactViewChange(isCompactView: boolean): void {
    this.screenService.setIsCompactView(isCompactView);
  }

  protected onHidePatientNameChange(hidePatientName: boolean): void {
    this.screenService.setHidePatientName(hidePatientName);
  }

  protected onOperatoryFilterChange({ value }: { value: string[] | string | null }): void {
    if (Array.isArray(value)) {
      this.screenService.setSelectedOperatoryIds({ selectedOperatoryIds: value });
      return;
    }

    if (value === null) {
      this.screenService.setSelectedOperatoryIds({ selectedOperatoryIds: [] });
      return;
    }

    this.screenService.setSelectedOperatoryIds({ selectedOperatoryIds: [value] });
  }

  protected onScheduleLegendClick(): void {
    const statusColors = this.store
      .selectSignal(selectStatusOptions)()
      .map((options) => options?.statusColor)
      .filter((statusColor): statusColor is AppointmentStatusColorV2 => statusColor !== undefined);

    this.dialog.open(ScheduleLegendDialogComponent, {
      backdropClass: ['bg-transparent'],
      hasBackdrop: true,
      disableClose: false,
      data: {
        appointmentStatusColors: statusColors,
        providers: this.screenService.internalProvidersSignal(),
      },
    });
  }

  private createInjector(): Injector {
    return Injector.create({
      providers: [
        { provide: MY_COLLABORATION_SIDEBAR_APPOINTMENT, useValue: this.selectedAppointment },
        { provide: MY_COLLABORATION_SIDEBAR_VISIBLE, useValue: this.sidebarVisible.asReadonly() },
      ],
      parent: this.injector,
    });
  }

  private createEventDetailsInjector(): Injector {
    return Injector.create({
      providers: [{ provide: EVENT_DETAILS_SIDEBAR_EVENT, useValue: this.selectedEvent }],
      parent: this.injector,
    });
  }
}
